import React, {
  useState,
  ReactNode,
  CSSProperties,
  useEffect,
  useRef,
} from 'react'

type PictureSource = {
  srcSet: string
  type?: string
  media?: string
}

type ProgressiveImageProps = {
  highSrc: string
  lowSrc: string
  highSources: PictureSource[]
  lowSources: PictureSource[]
  width: string
  height: string
  alt?: string
  wrapStyle?: CSSProperties
  imageStyle?: CSSProperties
  children?: ReactNode
}

/**
 * ProgressiveImage — компонент для прогрессивной загрузки изображений.
 *
 * Сначала отображает `lowSrc` с blur-эффектом,
 * затем плавно подменяет его на `highSrc` после загрузки.
 *
 * Если изображение уже есть в кэше браузера, `highSrc`
 * отображается сразу без анимации.
 *
 * Поддерживает адаптивные форматы изображений через
 * массивы `highSources` и `lowSources` (например: avif, webp, media-запросы).
 *
 * @example
 * ```tsx
 * <ProgressiveImage
 *   lowSrc="images/authBg/bg-low.png"
 *   highSrc="images/authBg/bg.jpg"
 *   highSources={[
 *      {srcSet: 'images/authBg/bg.avif', type: 'image/avif'},
 *      {srcSet: 'images/authBg/bg.webp', type: 'image/webp'},
 *   ]}
 *   lowSources={[{srcSet: 'images/authBg/bg-low.avif', type: 'image/avif'}]}
 *   width="3658"
 *   height="1542"
 *   wrapStyle={{
 *     position: 'absolute',
 *     inset: 0,
 *   }}
 *   imageStyle={{
 *     height: '100%',
 *     width: 'auto',
 *   }}
 *   alt="Example image"
 * />
 * ```
 */
const ProgressiveImage: React.FC<ProgressiveImageProps> = ({
  highSrc,
  lowSrc,
  highSources = [],
  lowSources = [],
  width,
  height,
  alt = '',
  wrapStyle,
  imageStyle,
  children,
}) => {
  const [isLoaded, setIsLoaded] = useState<boolean>(false)

  const highImageRef = useRef<HTMLImageElement | null>(null)

  useEffect(() => {
    const image = highImageRef.current
    if (image && image.complete) {
      setIsLoaded(true)
    }
  }, [])

  const renderSources = (src: string, sources: PictureSource[]) =>
    sources.map((source: PictureSource, index: number) => (
      <source
        key={index}
        srcSet={source.srcSet || src}
        type={source.type}
        media={source.media}
      />
    ))

  return (
    <div
      style={{
        position: 'relative',
        overflow: 'hidden',
        ...wrapStyle,
      }}>
      <picture
        style={{
          position: 'absolute',
          inset: 0,
        }}>
        {renderSources(lowSrc, lowSources)}

        <img
          src={lowSrc}
          width={width}
          height={height}
          alt={alt}
          draggable="false"
          style={{
            filter: 'blur(10px)',
            transition: 'opacity 0.6s ease',
            opacity: isLoaded ? 0 : 1,
            ...imageStyle,
          }}
        />
      </picture>

      <picture
        style={{
          position: 'absolute',
          inset: 0,
        }}>
        {renderSources(highSrc, highSources)}

        <img
          ref={highImageRef}
          src={highSrc}
          width={width}
          height={height}
          alt={alt}
          draggable="false"
          style={{
            position: 'absolute',
            inset: 0,
            transition: 'opacity 1s ease',
            opacity: isLoaded ? 1 : 0,
            ...imageStyle,
          }}
          onLoad={() => setIsLoaded(true)}
        />
      </picture>

      {children}
    </div>
  )
}

export default ProgressiveImage
